// Fake data keyed by referral code. Same shapes the real hooks return.
export const DEMO_CODE = ["CEWPTEST", "HEALOTEST1"];

const ISSUES_DEMO = {
  overallCount: 800,

  // PeopleDealingIssues expects an ARRAY with one object
  overall: [
    {
      "Mental Health Issues": 320,
      "Emotional Venting": 210,
      "Life Challenges": 170,
      "Personal Growth": 100,
    },
  ], // = 800

  "Mental Health Issues": { Anxiety: 130, Stress: 110, "Low Mood": 50, Burnout: 30 }, // = 320
  mentalHealthIssuesCount: 320,

  ventingIssues: { "Work Pressure": 90, Relationships: 70, "Team Conflict": 50 }, // = 210
  ventingIssuesCount: 210,

  lifeChalengesIssues: { Financial: 60, Family: 55, Health: 35, Relocation: 20 }, // = 170
  lifeChalengesIssuesCount: 170,

  personalGrowth: { Career: 45, Confidence: 30, Habits: 25 }, // = 100
  personalGrowthCount: 100,
};

// Shared by both demo codes. Totals line up across cards and chart.
const THERAPIST_DEMO = {
  data: {
    "overall Bookings": 50,          // total = 30 + 14 + 6
    TotalCounselling: 30,            // completed sessions
    upcomingAppointments: 14,        // scheduled, still to happen
    "Apponietment today": 6,         // typo matches what the component reads

    patientsgraph: {
      Weekly:  { "<18": 0, "18-25": 1, "26-35": 2, "36-45": 1, "46-60": 0, "61-80": 0 },  // = 4
      Monthly: { "<18": 1, "18-25": 3, "26-35": 4, "36-45": 2, "46-60": 2, "61-80": 0 },  // = 12
      Yearly:  { "<18": 2, "18-25": 8, "26-35": 9, "36-45": 6, "46-60": 4, "61-80": 1 },  // = 30
    },

    // Optional: shows the "Last year ±x%" pills on the cards
    growth: {
      overallBookings:      { value: "12%", trend: "up" },
      totalCounselling:     { value: "8%",  trend: "up" },
      upcomingAppointments: { value: "3%",  trend: "down" },
      appointmentsToday:    { value: "5%",  trend: "up" },
    },
  },
};

// ---------- Mood demo ----------
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function buildMoodDetails() {
  const today = new Date();

  // 60 daily points ending today (smooth wave around ~68, never below 45)
  const daily = Array.from({ length: 60 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (59 - i));
    const v = 68 + 9 * Math.sin(i / 4) + 4 * Math.cos(i / 1.7);
    return { date: ymd(d), organizationAvg: Math.round(v), hasData: true };
  });

  // Last year (all 12 months) + this year up to the current month
  const monthly = [];
  const y = today.getFullYear();
  for (const year of [y - 1, y]) {
    const last = year === y ? today.getMonth() + 1 : 12;
    for (let m = 1; m <= last; m++) {
      const base = year === y ? 70 : 64;
      monthly.push({
        month: `${year}-${pad(m)}`,
        organizationAvg: Math.round(base + 6 * Math.sin(m / 2)),
      });
    }
  }
  return { data: { result: [{ daily, monthly }] } };
}

// Keys must match EMOTION_META in Mood.jsx
const MOOD_PATTERNS = {
  data: {
    totalUsers: 1300,
    emotionsAvg: {
      anxiety: 62, sadness: 48, anger: 41, loneliness: 37, guilt: 29, joy: 71,
    },
  },
};

// Keys must match THEME_KEY_TO_LABEL in Mood.jsx
const THEME_BASE = [
  { key: "work", share: 34, subThemes: ["Deadlines", "Workload", "Manager pressure"] },
  { key: "relationShipRomance", share: 22, subThemes: ["Communication", "Breakups"] },
  { key: "family", share: 17, subThemes: ["Parents' expectations", "Family conflict"] },
  { key: "healthBody", share: 12, subThemes: ["Sleep", "Fatigue"] },
  { key: "financesCareer", share: 10, subThemes: ["Savings", "Career growth"] },
  { key: "socialLife", share: 5, subThemes: [] },
]; // shares sum to 100

function buildThemes(emotion) {
  const total = 40 + ((emotion.length * 7) % 60); // varies a bit per emotion
  const orderedThemes = THEME_BASE.map((t) => ({
    key: t.key,
    count: Math.max(1, Math.round((t.share / 100) * total)),
    subThemes: t.subThemes,
  }));
  return {
    data: {
      orderedThemes,
      totalCount: orderedThemes.reduce((s, t) => s + t.count, 0),
    },
  };
}

// Mimics fetch(url) -> Response for the mood endpoints
export function demoMoodFetch(url) {
  let body = { data: {} };
  if (url.includes("/moodDetails/")) body = buildMoodDetails();
  else if (url.includes("/moodPatterns/")) body = MOOD_PATTERNS;
  else if (url.includes("/moodThemeDetails/")) {
    const emotion = decodeURIComponent(url.split("/").pop());
    body = buildThemes(emotion);
  }
  return Promise.resolve({ ok: true, json: async () => body });
}

export const DEMO_DATA = {
  CEWPTEST: {
    // useUsers -> { users, employees }
    users: 1300,
    employees: 1500,

    // useIssuesData -> issuesData
    issuesData: { overallCount: 800 },

    // useRedFlag -> features (labelCounts must add up to totalCount)
    redFlag: {
      redFlagAttacks: {
        totalCount: 152,
        totalUsers: 1300,
        labelCounts: {
          "Self Harm": 5,
          "Severe Anxiety": 55,
          "Burnout": 72,
          "Substance Use": 20,
        },
      },
    },

    // useFeatures -> features
    features: { moodCount: 56, testCount: 95, selfCareCount: 68 },

    // useUsageLimits -> usage
    usage: {
      messageCount: 3587,
      messageLimit: 5000,
      messageLimitReached: false,
      isChatBotFree: false,
      therapyCount: 50,
      therapyLimit: 80,
      therapyLimitReached: false,
      isTherapyFree: false,
    },

    // useWorkerStats -> stats
    workerStats: {
      totalResponses: 1500,
      avgJobSatisfaction: 3.8,
      avgWorkLifeBalanceOutOf5: 3.6,
      genderCount: { Male: 600, Female: 900 }, // 40% / 60%
    },
    issuesData: ISSUES_DEMO,
    therapist: THERAPIST_DEMO,
    mood: true
  },
  HEALOTEST1: {
    // useUsers -> { users, employees }
    users: 1300,
    employees: 1500,

    // useIssuesData -> issuesData
    issuesData: { overallCount: 800 },

    // useRedFlag -> features (labelCounts must add up to totalCount)
    redFlag: {
      redFlagAttacks: {
        totalCount: 152,
        totalUsers: 1300,
        labelCounts: {
          "Self Harm": 5,
          "Severe Anxiety": 55,
          "Burnout": 72,
          "Substance Use": 20,
        },
      },
    },

    // useFeatures -> features
    features: { moodCount: 56, testCount: 95, selfCareCount: 68 },

    // useUsageLimits -> usage
    usage: {
      messageCount: 3587,
      messageLimit: 5000,
      messageLimitReached: false,
      isChatBotFree: false,
      therapyCount: 50,
      therapyLimit: 80,
      therapyLimitReached: false,
      isTherapyFree: false,
    },

    // useWorkerStats -> stats
    workerStats: {
      totalResponses: 1500,
      avgJobSatisfaction: 3.8,
      avgWorkLifeBalanceOutOf5: 3.6,
      genderCount: { Male: 600, Female: 900 }, // 40% / 60%
    },
    issuesData: ISSUES_DEMO,
    therapist: THERAPIST_DEMO,
    mood: true
  },
  
};



